// S9 App settings (/apps/$appId/settings/$section): section list about 25%, the section about 75%, "Widget off" in
// the header while the widget is off (root 6.9). No test mode (CT-027) and no "No hiding rules" warning: personal
// details are hidden automatically, so no app needs a rule (CT-028). Editors and Admins edit
// (AppView.can); there are no per-app roles or groups to set (poc CT-008), and the fallback owner is in
// General. Linear: the team and project the app's tickets are handed to (poc CT-009). No "Who sees the widget"
// section (CT-026): the drawer follows the site's own access.
import type { AppView } from '@feedback/shared/contracts/apps';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { appQuery } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { errorText } from '../lib/admin-api';
import { Badge, Loading, Notice, PageTitle } from '../ui/primitives';
import { AiSection } from './sections/AiSection';
import { EnvironmentsSection } from './sections/EnvironmentsSection';
import { GeneralSection } from './sections/GeneralSection';
import { InstallSection } from './sections/InstallSection';
import { LinearSection } from './sections/LinearSection';
import { MaskingSection } from './sections/MaskingSection';
import { RoutingSection } from './sections/RoutingSection';
import { TabSection } from './sections/TabSection';

export const SETTINGS_SECTIONS = [
  { slug: 'general', label: 'General' },
  { slug: 'environments', label: 'Environments and addresses' },
  { slug: 'routing', label: 'Routing' },
  { slug: 'masking', label: 'Hiding rules' },
  { slug: 'ai', label: 'AI' },
  { slug: 'install', label: 'Install' },
  { slug: 'tab', label: 'Tab and switch off' },
  { slug: 'linear', label: 'Linear' },
] as const;
export type SettingsSectionSlug = (typeof SETTINGS_SECTIONS)[number]['slug'];

export function isSettingsSection(value: string): value is SettingsSectionSlug {
  return SETTINGS_SECTIONS.some((s) => s.slug === value);
}

const SECTION_COMPONENTS: Record<SettingsSectionSlug, (props: { app: AppView }) => ReactNode> = {
  general: GeneralSection,
  environments: EnvironmentsSection,
  routing: RoutingSection,
  masking: MaskingSection,
  ai: AiSection,
  install: InstallSection,
  tab: TabSection,
  linear: LinearSection,
};

export function AppSettingsPage({
  appId,
  section,
  registered,
}: {
  appId: string;
  section: SettingsSectionSlug;
  registered: boolean;
}) {
  const app = useQuery(appQuery(appId));
  const Section = SECTION_COMPONENTS[section];
  const label = SETTINGS_SECTIONS.find((s) => s.slug === section)?.label ?? '';

  if (!app.data) {
    return app.isError ? (
      <Notice tone="danger" title="The app’s settings couldn’t be shown">
        {errorText(app.error)}
      </Notice>
    ) : (
      <Loading label="Loading the app’s settings" />
    );
  }
  const view = app.data;

  return (
    <div>
      <PageTitle
        crumbs={
          <nav aria-label="Breadcrumb">
            <Link to="/apps" className="underline">
              Apps
            </Link>{' '}
            &gt; {view.name} &gt; Settings
          </nav>
        }
        title={`${view.name} settings`}
        badge={view.widgetState === 'off' ? <Badge tone="danger">Widget off</Badge> : null}
      />

      {registered ? (
        <Notice tone="success" className="mb-3" title="Your app is registered">
          Install the widget below. You’re its fallback owner; the rest of its settings are in the sections on the left.
        </Notice>
      ) : null}
      {view.can.manageSettings ? null : (
        <Notice className="mb-3">You can see these settings. Editors and Admins change them.</Notice>
      )}

      <div className="grid gap-4 md:grid-cols-[minmax(12rem,1fr)_3fr]">
        <nav aria-label="Settings sections">
          <ul className="flex flex-wrap gap-1 md:flex-col">
            {SETTINGS_SECTIONS.map((s) => (
              <li key={s.slug}>
                <Link
                  to="/apps/$appId/settings/$section"
                  params={{ appId, section: s.slug }}
                  search={{}}
                  aria-current={s.slug === section ? 'page' : undefined}
                  className={cn(
                    'block rounded-md px-3 py-2 text-sm',
                    s.slug === section
                      ? 'bg-primary font-semibold text-on-primary'
                      : 'border border-border bg-surface hover:bg-surface-raised md:border-transparent md:bg-transparent',
                  )}
                >
                  {s.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <section className="min-w-0" aria-label={label}>
          <Section app={view} />
        </section>
      </div>
    </div>
  );
}
